import { RefCallback, useCallback, useEffect, useState } from 'react';

/**
 * Custom hook to determine if the current scrollY is below the ref element.
 *
 * @param {boolean} isRefVisibleInitially - Initial visibility state of the element (ref). Defaults to false.
 * @returns {object} An object containing:
 *  - `isBelow`: A boolean indicating if the current scrollY is below the element.
 *  - `ref`: A ref callback function to be attached to the element to monitor.
 */
export function useScrollBelow(isRefVisibleInitially: boolean = false): {
    isBelow: boolean;
    ref: RefCallback<HTMLElement>;
} {
    const [isBelow, setIsBelow] = useState(isRefVisibleInitially);
    const [refElement, setRefElement] = useState<HTMLElement | null>(null);

    const setRef = useCallback((node: HTMLElement | null) => {
        if (node !== null) {
            setRefElement(node);
        }
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            if (refElement) {
                const elementTop = refElement.getBoundingClientRect().top + window.scrollY;
                setIsBelow(window.scrollY > elementTop);
            }
        };

        window.addEventListener('scroll', handleScroll);
        handleScroll(); // Initial check

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [refElement]);

    return { isBelow, ref: setRef };
}
